import React from 'react';
import useAsyncHandler from '../../../../hooks/use-async-handler';
import {
  BaseKenticoData,
  loadKenticoData,
} from '../../../../utils/remote-config-manager';
import { KenticoDataContextProvider } from '../../../../contexts/kentico-data-context';
import { LoadingScreen } from '../../loading-screen';

interface ILandingDataProviderProps {
  children: React.ReactNode;
}

/**
 * Empty call center placeholder for the public landing page. Call center data
 * requires authenticated person context, so it is intentionally left blank
 * here; the provider skips its open/closed calculation when no hours of
 * operation are present.
 */
const emptyCallCenter = {
  closures: [],
  hoursOfOperation: { start: '', end: '' },
};

/**
 * Supplies the environment-level Kentico data (assets, config, etc.) to the
 * public landing page via {@link KenticoDataContextProvider}, mirroring how
 * {@link AppStartupHandler} wires the provider for the authenticated app.
 *
 * The underlying {@link loadKenticoData} call is served from the
 * remote-config-manager cache, so the same fetches made later by the /app
 * startup sequence are de-duplicated rather than repeated.
 */
const LandingDataProvider = (props: Readonly<ILandingDataProviderProps>) => {
  const { data, error, started, startRequest } =
    useAsyncHandler<BaseKenticoData>(loadKenticoData);

  if (!started) {
    startRequest();
  }

  // While the Kentico data loads, show the loading screen. On failure we still
  // render the landing with empty data so it degrades gracefully (assets are
  // accessed with optional chaining).
  if (!data && !error) {
    return <LoadingScreen />;
  }

  const kentico = data ?? {
    assets: {},
    featureFlags: {} as BaseKenticoData['featureFlags'],
    config: {},
    localizedStrings: {},
    dynamicContent: {},
  };

  return (
    <KenticoDataContextProvider
      assets={kentico.assets}
      callCenter={emptyCallCenter}
      featureFlags={kentico.featureFlags}
      config={kentico.config}
      localizedStrings={kentico.localizedStrings}
      dynamicContent={kentico.dynamicContent}
    >
      {props.children}
    </KenticoDataContextProvider>
  );
};

export { LandingDataProvider };
